<script setup>
// The Trail map block's map, drawn in the page rather than framed.
//
// It used to be an iframe of the network's public page: that page's
// header, fonts and colors inside the organization's own site, and a
// scroll inside a scroll. Both templates render this instead, and it
// takes its look from the block around it — font inherited, colors
// from the --stm-* properties each template sets — so it reads as
// part of the site.
//
// Two renderings. On the free TrailsIQ map the server names a style
// and this mounts a live MapLibre map; on any other engine (a provider
// meter, and this is anonymous traffic) it draws the network's outline
// as an SVG, the same answer the public network page gives. Either way
// a visitor can pick a trail and see its status.
//
// The trails are fetched when the block nears the viewport rather than
// shipped with every page of the site. See SiteMap on the server.
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { fmtDistance } from '@/lib/units.js';
import { onCanonicalHost, sitePrefix } from './useSiteForm.js';
import { mapLineColor, mapLook, outlineGeometry, siteMapUrl, statusCounts } from './siteTrailMap.js';

const props = defineProps({
    config: { type: Object, required: true },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
    network: { type: Object, default: null },
});

const editing = inject('tiqEditorCanvas', false);

const root = ref(null);
const mapEl = ref(null);
const data = shallowRef(null);
const state = ref('idle'); // idle | loading | ready | error
// wantLive: the server named a style, so a live map is being mounted.
// live: it loaded. The outline stays up until then, so the frame is
// never empty, and stays the answer if the map cannot start at all.
const wantLive = ref(false);
const live = ref(false);
const selectedId = ref(null);

const tall = computed(() => props.config.height === 'tall');
const colorBy = computed(() => (props.config.color_by === 'trail' || !data.value?.show_status ? 'trail' : 'status'));
const trails = computed(() => (data.value?.trails || []).map((t) => ({ ...t, line: mapLineColor(t, colorBy.value) })));
const selected = computed(() => trails.value.find((t) => t.id === selectedId.value) || null);
const counts = computed(() => statusCounts(trails.value));
const showList = computed(() => props.config.show_list !== false && trails.value.length > 0);
const outline = computed(() => outlineGeometry(trails.value, tall.value ? 600 : 350));

const url = computed(() => siteMapUrl({
    orgSlug: props.org?.slug,
    networkId: props.network?.id,
    look: mapLook(props.config),
    canonical: editing || onCanonicalHost(),
    hostPrefix: sitePrefix(),
}));

// The trail's page on this website. A visitor's page rewrites site
// paths itself (Show.vue), so the path goes out as written; the editor
// has no such handler, so it gets the /site/{slug} address.
function trailHref(t) {
    if (!t?.href) return null;
    return editing && props.org?.slug ? `/site/${props.org.slug}${t.href}` : t.href;
}

function distanceLabel(t) {
    if (!t?.distance_m) return '';
    return fmtDistance(t.distance_m / 1000, data.value?.units || 'metric');
}

const STATUS_WORD = { open: 'Open', caution: 'Caution', closed: 'Closed' };

// ── Fetch ──────────────────────────────────────────────────────────
let observer = null;
let seq = 0;
async function load() {
    if (!url.value) { data.value = null; state.value = 'idle'; return; }
    const mine = ++seq;
    state.value = 'loading';
    try {
        const res = await fetch(url.value, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (mine !== seq) return;
        data.value = body;
        state.value = 'ready';
    } catch {
        if (mine !== seq) return;
        data.value = null;
        state.value = 'error';
    }
}

function startWhenVisible() {
    if (typeof window === 'undefined' || !root.value) return;
    if (!('IntersectionObserver' in window)) { load(); return; }
    observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
            observer?.disconnect();
            observer = null;
            load();
        }
    }, { rootMargin: '400px 0px' });
    observer.observe(root.value);
}

onMounted(startWhenVisible);
watch(url, (next, prev) => { if (next !== prev && state.value !== 'idle') load(); });

// ── Live map ───────────────────────────────────────────────────────
let map = null;
let lib = null;

function webglAvailable() {
    try {
        const c = document.createElement('canvas');
        return !!(c.getContext('webgl2') || c.getContext('webgl'));
    } catch {
        return false;
    }
}

function featureCollection() {
    return {
        type: 'FeatureCollection',
        features: trails.value.map((t) => ({
            type: 'Feature',
            id: t.id,
            properties: { id: t.id, name: t.name, line: t.line, bucket: t.bucket },
            geometry: { type: 'MultiLineString', coordinates: t.lines },
        })),
    };
}

function bounds() {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const t of trails.value) {
        for (const line of t.lines) {
            for (const [x, y] of line) {
                if (x < minX) minX = x; if (x > maxX) maxX = x;
                if (y < minY) minY = y; if (y > maxY) maxY = y;
            }
        }
    }
    return Number.isFinite(minX) ? [[minX, minY], [maxX, maxY]] : null;
}

// A symbol layer added by hand needs a text-font the glyph server has:
// take it from a label the basemap itself draws (see CLAUDE.md).
function labelFont() {
    try {
        for (const l of map.getStyle()?.layers || []) {
            const f = l.type === 'symbol' ? l.layout?.['text-font'] : null;
            if (Array.isArray(f) && f.every((x) => typeof x === 'string')) return f;
        }
    } catch { /* fall through */ }
    return null;
}

function drawTrails() {
    if (!map) return;
    const src = map.getSource('stm-trails');
    if (src) { src.setData(featureCollection()); return; }
    map.addSource('stm-trails', { type: 'geojson', data: featureCollection() });
    const lineLayout = { 'line-cap': 'round', 'line-join': 'round' };
    map.addLayer({ id: 'stm-casing', type: 'line', source: 'stm-trails', layout: lineLayout,
        paint: { 'line-color': '#ffffff', 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 4, 15, 8], 'line-opacity': 0.85 } });
    map.addLayer({ id: 'stm-selected', type: 'line', source: 'stm-trails', layout: lineLayout,
        filter: ['==', ['get', 'id'], -1],
        paint: { 'line-color': ['get', 'line'], 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 9, 15, 14], 'line-opacity': 0.3 } });
    map.addLayer({ id: 'stm-line', type: 'line', source: 'stm-trails', layout: lineLayout,
        paint: { 'line-color': ['get', 'line'], 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 2.5, 15, 4.5] } });
    // Closed reads as broken line as well as red, for a reader who
    // cannot tell the colors apart.
    map.addLayer({ id: 'stm-closed', type: 'line', source: 'stm-trails',
        filter: ['==', ['get', 'bucket'], 'closed'],
        layout: { 'line-cap': 'butt', 'line-join': 'round' },
        paint: { 'line-color': '#ffffff', 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 1, 15, 2], 'line-dasharray': [2, 2] } });
    map.addLayer({ id: 'stm-hit', type: 'line', source: 'stm-trails',
        paint: { 'line-color': '#000000', 'line-width': 18, 'line-opacity': 0 } });
    const font = labelFont();
    if (font) {
        map.addLayer({ id: 'stm-label', type: 'symbol', source: 'stm-trails', minzoom: 12.5,
            layout: { 'symbol-placement': 'line', 'text-field': ['get', 'name'], 'text-font': font, 'text-size': 12, 'text-max-angle': 30 },
            paint: { 'text-color': '#1f2a24', 'text-halo-color': '#ffffff', 'text-halo-width': 1.6 } });
    }
    paintSelection();
}

// A picked trail stands out by the others stepping back, which reads
// on any basemap; a halo alone vanished on the green one.
function paintSelection() {
    if (!map?.getLayer('stm-selected')) return;
    const sel = selectedId.value ?? -1;
    map.setFilter('stm-selected', ['==', ['get', 'id'], sel]);
    const opacity = sel === -1 ? 1 : ['case', ['==', ['get', 'id'], sel], 1, 0.35];
    for (const id of ['stm-line', 'stm-casing', 'stm-closed']) {
        if (map.getLayer(id)) map.setPaintProperty(id, 'line-opacity', id === 'stm-casing' && sel === -1 ? 0.85 : opacity);
    }
}

async function mountLive() {
    if (map || !data.value?.style_url || !mapEl.value || !webglAvailable()) return false;
    try {
        lib = (await import('maplibre-gl')).default;
        await import('maplibre-gl/dist/maplibre-gl.css');
    } catch {
        return false;
    }
    if (!mapEl.value) return false;
    const fit = bounds();
    try {
        map = new lib.Map({
            container: mapEl.value,
            style: new URL(data.value.style_url, window.location.origin).href,
            bounds: fit || undefined,
            fitBoundsOptions: { padding: 40, maxZoom: 15 },
            center: fit ? undefined : [0, 20],
            zoom: fit ? undefined : 1,
            // A page scrolls past this map far more often than it pans
            // it: one finger (or a plain wheel) scrolls the page, two
            // fingers (or ctrl + wheel) move the map.
            cooperativeGestures: true,
            dragRotate: false,
            pitchWithRotate: false,
            attributionControl: { compact: true },
        });
        map.touchZoomRotate?.disableRotation?.();
        map.addControl(new lib.NavigationControl({ showCompass: false }), 'top-right');
    } catch {
        map = null;
        return false;
    }
    // This component owns its map for its whole life and nothing moves
    // its canvas, so the plain load event is safe here.
    map.on('load', () => { drawTrails(); live.value = true; });
    map.on('click', 'stm-hit', (e) => {
        const id = e.features?.[0]?.properties?.id;
        if (id !== undefined) selectedId.value = Number(id);
    });
    map.on('click', (e) => {
        const hits = map.queryRenderedFeatures(e.point, { layers: ['stm-hit'] });
        if (!hits.length) selectedId.value = null;
    });
    map.on('mouseenter', 'stm-hit', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'stm-hit', () => { map.getCanvas().style.cursor = ''; });
    // Coverage follows trail corridors; a missing tile past the edge is
    // the basemap working as designed and must not surface as an error.
    map.on('error', () => {});
    return true;
}

watch(data, async () => {
    if (!data.value) return;
    if (map) { drawTrails(); fitAll(); return; }
    wantLive.value = !!data.value.style_url;
    if (!wantLive.value) return;
    await nextTick();
    if (!(await mountLive())) wantLive.value = false;
});
watch(colorBy, () => { if (map?.getSource('stm-trails')) drawTrails(); });
watch(selectedId, paintSelection);

function fitAll() {
    const b = bounds();
    if (map && b) map.fitBounds(b, { padding: 40, maxZoom: 15, duration: 0 });
}

function pick(t) {
    selectedId.value = selectedId.value === t.id ? null : t.id;
    if (!map || selectedId.value === null) return;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const line of t.lines) for (const [x, y] of line) {
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    if (Number.isFinite(minX)) map.fitBounds([[minX, minY], [maxX, maxY]], { padding: 60, maxZoom: 15.5, duration: 600 });
}

onBeforeUnmount(() => {
    observer?.disconnect();
    try { map?.remove(); } catch { /* already gone */ }
    map = null;
});
</script>

<template>
    <div ref="root" class="tiq-stm" :class="{ 'tiq-stm--live': live }">
        <div class="tiq-stm__stage">
            <div class="tiq-stm__frame" :class="[tall ? 'tiq-stm__frame--tall' : 'tiq-stm__frame--wide', { 'has-selection': selected }]">
                <div v-if="wantLive" ref="mapEl" class="tiq-stm__map"></div>
                <!-- Drawn while the live map loads too, so the frame is never
                     empty; it steps aside once the map has painted. -->
                <svg v-if="!live && outline" class="tiq-stm__outline" :viewBox="outline.viewBox" preserveAspectRatio="xMidYMid meet"
                     role="img" :aria-label="`Map of ${data?.network?.name || 'the trails'}`">
                    <g v-for="t in outline.trails" :key="t.id" class="tiq-stm__trail"
                       :class="{ 'is-selected': t.id === selectedId, 'is-closed': t.bucket === 'closed' }"
                       @click="pick(t)">
                        <polyline v-for="(pts, i) in t.paths" :key="`h${i}`" :points="pts" class="tiq-stm__hit" />
                        <polyline v-for="(pts, i) in t.paths" :key="`c${i}`" :points="pts" class="tiq-stm__casing" />
                        <polyline v-for="(pts, i) in t.paths" :key="`l${i}`" :points="pts" class="tiq-stm__line" :stroke="t.line" />
                    </g>
                </svg>
                <div v-if="!network && editing" class="tiq-stm__empty">
                    <strong>Trail map</strong>
                    <span>Add a public network with trails to show its map here.</span>
                </div>
                <div v-else-if="network && state === 'error'" class="tiq-stm__empty">
                    <strong>The map could not be loaded</strong>
                    <span>Try again in a moment.</span>
                </div>
                <div v-else-if="state === 'ready' && !trails.length" class="tiq-stm__empty">
                    <strong>{{ data?.network?.name || 'Trail map' }}</strong>
                    <span>None of this network's public trails has a line to draw yet.</span>
                </div>
                <div v-else-if="state !== 'ready'" class="tiq-stm__empty tiq-stm__empty--quiet">
                    <span>Loading the map…</span>
                </div>

                <!-- In the editor a click selects the block rather than the map. -->
                <div v-if="editing" class="tiq-stm__shield" aria-hidden="true"></div>
            </div>

            <!-- Over the map's corner on a wide block; under it on a narrow
                 one, where a card over the map would hide the map. -->
            <div v-if="selected" class="tiq-stm__card" role="status">
                <button type="button" class="tiq-stm__close" aria-label="Close" @click="selectedId = null">×</button>
                <p class="tiq-stm__card-name">
                    <span class="tiq-stm__dot" :style="{ background: selected.line }"></span>
                    {{ selected.name }}
                </p>
                <p class="tiq-stm__card-meta">
                    <span v-if="selected.season_state === 'closed'" class="tiq-stm__pill">Closed for winter</span>
                    <span v-else-if="data?.show_status" class="tiq-stm__pill" :class="`tiq-stm__pill--${selected.bucket}`">{{ STATUS_WORD[selected.bucket] }}</span>
                    <span v-if="distanceLabel(selected)">{{ distanceLabel(selected) }}</span>
                    <span v-if="selected.difficulty" class="tiq-stm__cap">{{ selected.difficulty }}</span>
                </p>
                <p v-if="data?.show_status && selected.note" class="tiq-stm__card-note">{{ selected.note }}</p>
                <a v-if="trailHref(selected)" :href="trailHref(selected)" class="tiq-stm__card-link">Trail details →</a>
            </div>
        </div>

        <div v-if="colorBy === 'status' && trails.length" class="tiq-stm__legend">
            <span v-for="k in ['open', 'caution', 'closed']" :key="k" v-show="counts[k]" class="tiq-stm__legend-item">
                <span class="tiq-stm__swatch" :class="`tiq-stm__swatch--${k}`"></span>{{ counts[k] }} {{ STATUS_WORD[k].toLowerCase() }}
            </span>
        </div>

        <ul v-if="showList" class="tiq-stm__list">
            <li v-for="t in trails" :key="t.id">
                <button type="button" class="tiq-stm__chip" :class="{ 'is-selected': t.id === selectedId }"
                        :aria-pressed="t.id === selectedId" @click="pick(t)">
                    <span class="tiq-stm__dot" :style="{ background: t.line }"></span>{{ t.name }}
                </button>
            </li>
        </ul>
    </div>
</template>

<style scoped>
.tiq-stm { color: var(--stm-ink, inherit); container-type: inline-size; }
.tiq-stm__stage { position: relative; }
.tiq-stm__frame {
    position: relative;
    /* A definite width, or min-height runs back through the aspect
       ratio and widens the frame past a phone's screen. */
    width: 100%;
    min-height: 280px;
    overflow: hidden;
    border-radius: var(--stm-radius, 8px);
    border: 1px solid var(--stm-line, rgba(0, 0, 0, 0.12));
    background: var(--stm-paper, #f4f1e8);
}
.tiq-stm__frame--wide { aspect-ratio: 16 / 7; }
.tiq-stm__frame--tall { aspect-ratio: 4 / 3; }
.tiq-stm__map, .tiq-stm__outline { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
.tiq-stm__outline {
    background-image:
        linear-gradient(var(--stm-grid, rgba(0, 0, 0, 0.05)) 1px, transparent 1px),
        linear-gradient(90deg, var(--stm-grid, rgba(0, 0, 0, 0.05)) 1px, transparent 1px);
    background-size: 48px 48px;
}
.tiq-stm__trail { cursor: pointer; }
.tiq-stm__trail polyline { fill: none; stroke-linecap: round; stroke-linejoin: round; vector-effect: non-scaling-stroke; }
.tiq-stm__hit { stroke: transparent; stroke-width: 18; }
.tiq-stm__casing { stroke: #fff; stroke-width: 7; opacity: 0.9; }
.tiq-stm__line { stroke-width: 3.5; }
.tiq-stm__trail.is-closed .tiq-stm__line { stroke-dasharray: 6 5; }
.tiq-stm__trail.is-selected .tiq-stm__casing { stroke-width: 11; }
.tiq-stm__trail.is-selected .tiq-stm__line { stroke-width: 5.5; }
.tiq-stm__trail:hover .tiq-stm__line { stroke-width: 5; }
.has-selection .tiq-stm__trail:not(.is-selected) { opacity: 0.35; }

.tiq-stm__empty {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 24px;
    text-align: center;
    color: var(--stm-muted, #6b7280);
    font-size: 14px;
}
.tiq-stm__empty strong { color: var(--stm-ink, inherit); font-size: 16px; }
.tiq-stm__empty--quiet { font-size: 13px; }

.tiq-stm__card {
    position: absolute;
    left: 12px;
    bottom: 12px;
    z-index: 2;
    width: min(320px, calc(100% - 24px));
    padding: 14px 40px 14px 16px;
    border-radius: var(--stm-radius, 8px);
    background: var(--stm-surface, #fff);
    color: var(--stm-ink, inherit);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
}
.tiq-stm__card p { margin: 0; }
.tiq-stm__card-name { display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 16px; line-height: 1.25; }
.tiq-stm__card-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px; margin-top: 8px !important; font-size: 13px; color: var(--stm-muted, #6b7280); }
.tiq-stm__card-note { margin-top: 8px !important; font-size: 13px; line-height: 1.45; }
.tiq-stm__card-link { display: inline-block; margin-top: 10px; font-size: 13px; font-weight: 600; color: var(--stm-accent, currentColor); text-decoration: none; }
.tiq-stm__card-link:hover { text-decoration: underline; }
.tiq-stm__cap { text-transform: capitalize; }
@container (max-width: 520px) {
    .tiq-stm__card {
        position: relative;
        left: auto;
        bottom: auto;
        width: 100%;
        margin-top: 12px;
        box-shadow: none;
        border: 1px solid var(--stm-line, rgba(0, 0, 0, 0.12));
    }
}
.tiq-stm__close {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--stm-muted, #6b7280);
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
}
.tiq-stm__close:hover { background: rgba(0, 0, 0, 0.06); }

.tiq-stm__pill { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 999px; font-size: 12px; font-weight: 600; }
.tiq-stm__pill--open { background: #e3f1e6; color: #1d6b35; }
.tiq-stm__pill--caution { background: #fbf0d9; color: #7d5200; }
.tiq-stm__pill--closed { background: #fbe3df; color: #9b2c1d; }

.tiq-stm__dot { width: 10px; height: 10px; border-radius: 50%; flex: none; display: inline-block; }
.tiq-stm__legend { display: flex; flex-wrap: wrap; gap: 6px 18px; margin-top: 12px; font-size: 13px; color: var(--stm-muted, #6b7280); }
.tiq-stm__legend-item { display: inline-flex; align-items: center; gap: 6px; }
.tiq-stm__swatch { width: 18px; height: 4px; border-radius: 2px; display: inline-block; }
.tiq-stm__swatch--open { background: #2e9b4f; }
.tiq-stm__swatch--caution { background: #d99a1e; }
.tiq-stm__swatch--closed { background: repeating-linear-gradient(90deg, #c2412d 0 5px, transparent 5px 8px); }

.tiq-stm__list { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0 0; padding: 0; list-style: none; }
.tiq-stm__chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border: 1px solid var(--stm-line, rgba(0, 0, 0, 0.12));
    border-radius: 999px;
    background: var(--stm-surface, #fff);
    color: var(--stm-ink, inherit);
    font: inherit;
    font-size: 13px;
    cursor: pointer;
}
.tiq-stm__chip:hover { border-color: var(--stm-accent, currentColor); }
.tiq-stm__chip.is-selected { border-color: var(--stm-accent, currentColor); box-shadow: inset 0 0 0 1px var(--stm-accent, currentColor); }

.tiq-stm__shield { position: absolute; inset: 0; z-index: 3; background: transparent; cursor: pointer; }
</style>
